import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { schemas } from '@shared/index';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormActions, FormField } from '@/components/inputs/Form';
import { paths } from '@/routes/paths';
import { AuthCard } from './AuthLayout';
import { authErrorMessage, useAuthActions, useAuthState } from './useAuth';

type LoginValues = z.infer<typeof schemas.identity.loginBody>;

export function LoginPage() {
  const { login } = useAuthActions();
  const { expiredReason } = useAuthState();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  // The schema is the SAME object the server validates against, so the rules cannot
  // drift between the two.
  const form = useForm<LoginValues>({
    resolver: zodResolver(schemas.identity.loginBody),
    defaultValues: { email: '', password: '' },
  });

  const returnTo = (location.state as { from?: string } | null)?.from ?? paths.dashboard;

  return (
    <AuthCard
      title="Sign in"
      description="Use your Bemedic Academy work account."
      footer={
        <Link to={paths.forgotPassword} className="text-link hover:underline">
          Forgot your password?
        </Link>
      }
    >
      {expiredReason && !formError && (
        <div
          role="status"
          className="mb-4 flex items-start gap-2 rounded-md border border-line bg-subtle p-3 text-caption text-fg-muted"
        >
          <AlertCircle aria-hidden className="mt-px size-4 shrink-0" />
          {expiredReason}
        </div>
      )}

      {formError && (
        // `role="alert"` so the failure is announced; a visual-only message leaves a
        // screen-reader user pressing Sign in with no feedback at all.
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-md border border-danger-fill bg-danger-bg p-3 text-caption text-danger"
        >
          <AlertCircle aria-hidden className="mt-px size-4 shrink-0" />
          {formError}
        </div>
      )}

      <Form
        form={form}
        onSubmit={async (values) => {
          setFormError(null);
          try {
            const session = await login(values.email, values.password);
            navigate(
              session.mustChangePassword ? paths.changePassword : returnTo,
              { replace: true },
            );
          } catch (err) {
            // One message for a wrong password and an unknown address alike — the
            // server deliberately does not distinguish them, and neither should we.
            setFormError(authErrorMessage(err));
            form.resetField('password');
          }
        }}
      >
        <FormField name="email" label="Work email" required>
          {(field) => (
            <Input
              {...field}
              {...form.register('email')}
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="you@bemedicacademy.com"
            />
          )}
        </FormField>

        <FormField name="password" label="Password" required>
          {(field) => (
            <Input
              {...field}
              {...form.register('password')}
              type="password"
              autoComplete="current-password"
            />
          )}
        </FormField>

        <FormActions className="sm:justify-stretch">
          <Button
            type="submit"
            variant="primary"
            className="h-10 w-full"
            loading={form.formState.isSubmitting}
          >
            Sign in
          </Button>
        </FormActions>
      </Form>
    </AuthCard>
  );
}
